import { KeyPrefixActionitem, createPrefixedKey } from '@nucleus/ncu-key-prefix';
import {
  CalendarItem,
  InfoButtonsLayout,
  InfoButtonsWidth,
  LookConfig,
  Media,
  MediaOptions,
  PresetMedia,
  PresetMediaKey,
  SectionAction,
  SectionLayout,
} from '@nucleus/types/web';
import { BuildSectionConfig, ExtractPayload, SectionTypeEnum } from '@nucleus/types/web/section-data';
import { merge, omit } from 'lodash';
import { blockBackground } from '../../config/presetmedia/block-background';
import { cardBackground } from '../../config/presetmedia/cardbackground';
import { cardMediaFull } from '../../config/presetmedia/cardmediafull';
import { cardMediaInline } from '../../config/presetmedia/cardmediainline';
import { leaderCardMediaFull } from '../../config/presetmedia/leadercardmediafull';
import { leaderCardMediaInline } from '../../config/presetmedia/leadercardmediainline';
import { listMedia } from '../../config/presetmedia/listmedia';
import { sectionBackground } from '../../config/presetmedia/sectionbackground';
import { sectionMedia } from '../../config/presetmedia/sectionmedia';

const presetMediaMap: Record<PresetMediaKey, PresetMedia> = {
  blockBackground: blockBackground,
  cardBackground: cardBackground,
  cardMediaFull: cardMediaFull,
  cardMediaInline: cardMediaInline,
  leaderCardMediaFull: leaderCardMediaFull,
  leaderCardMediaInline: leaderCardMediaInline,
  listMedia: listMedia,
  sectionBackground: sectionBackground,
  sectionMedia: sectionMedia,
};

export function buildPresetMedia(
  presetMediaKey: PresetMediaKey,
  lookConfig?: LookConfig,
  mediaItem: Partial<Media> | undefined = {}
): Media {
  let preset = lookConfig?.presetMedia?.[presetMediaKey];
  preset ??= presetMediaMap[presetMediaKey];

  return merge(
    {
      presetMediaKey: presetMediaKey,
    },
    mediaItem,
    omit(preset.mediaOptions, 'image.fileId', 'video.image.fileId')
  );
}

export function buildPlaceholderAction(
  text = 'Link this Button',
  displayAs: SectionAction['displayAs'] = 'style1'
): SectionAction {
  return {
    id: createPrefixedKey(KeyPrefixActionitem),
    title: text,
    type: 'none',
    displayAs: displayAs,
    openInNewTab: false,
  };
}

export function buildPlaceholderMedia(mediaItem: Partial<Media> | undefined = {}): Media {
  return merge(
    {},
    {
      useColor: false,
      useImage: false,
      useVideo: false,
      image: {
        scale: 'cover',
        tile: 'none',
      },
      blending: {
        opacity: 1,
        blendMode: 'none',
        fade: 'none',
      },
    },
    {
      ...mediaItem,
      image: mediaItem.image,
    }
  );
}

export const buildCalendarItem = (): CalendarItem => ({
  id: createPrefixedKey('calendar'),
  feedUrl: '',
  displayData: ['description', 'date', 'location'],
  displayBy: 'week',
  limit: 5,
});

export const blockMediaOptionsDefault = (lookConfig?: LookConfig): MediaOptions =>
  buildPresetMedia('sectionMedia', lookConfig, {
    useColor: false,
    useImage: true,
    useVideo: false,
  });

export const cardMediaOptionsBackgroundDefault = (lookConfig?: LookConfig): MediaOptions =>
  buildPresetMedia('cardMediaFull', lookConfig, {
    useColor: false,
    useImage: true,
    useVideo: false,
  });

export const cardMediaOptionsInlineDefault = (lookConfig?: LookConfig): MediaOptions =>
  buildPresetMedia('cardMediaInline', lookConfig, {
    useColor: false,
    useImage: true,
    useVideo: false,
  });

export const listMediaOptionsInlineDefault = (lookConfig?: LookConfig): MediaOptions =>
  buildPresetMedia('listMedia', lookConfig, {
    useColor: false,
    useImage: true,
    useVideo: false,
  });

export const leaderMediaOptionsBackgroundDefault = (lookConfig?: LookConfig): MediaOptions =>
  buildPresetMedia('leaderCardMediaFull', lookConfig, {
    useColor: false,
    useImage: true,
    useVideo: false,
  });

export const leaderMediaOptionsInlineDefault = (lookConfig?: LookConfig): MediaOptions =>
  buildPresetMedia('leaderCardMediaInline', lookConfig, {
    useColor: false,
    useImage: true,
    useVideo: false,
  });

export const StandardSectionBackground = {
  backgroundMedia: { presetMediaKey: 'sectionBackground', presetMediaOrder: 'first' },
  blockBackgroundMedia: { presetMediaKey: 'blockBackground', presetMediaOrder: 'first' },
} as const;

export const StandardBlockButtons = {
  blockButtonsLayout: InfoButtonsLayout.Vertical,
  blockButtonsWidth: InfoButtonsWidth.Auto,
};

export function buildStandardWrapper<T extends SectionTypeEnum>(
  config: BuildSectionConfig,
  layoutPayload: Partial<ExtractPayload<T>>
): ExtractPayload<T> {
  return {
    name: config.title,
    blockLayout: config.layout,
    layout: SectionLayout.Full,
    blockMediaOptions: blockMediaOptionsDefault(config.lookConfig),
    ...layoutPayload,
  } as ExtractPayload<T>;
}
